import { moment, unit } from 'Imports';
import * as DateFormatter from '$Components/Shared/DateFormatter';
import { getStatusLabel } from '$Utilities/videoEventStatusUtility';
import { VideoEventCommentResponse, VideoEventFilterMultiselectRequest, VideoEventWorkflowStatusIdEnum } from '$Generated/api';

export interface CsvData {
    EventType: string | undefined;
    CompanyLevel?: string | undefined;
    DisplayId: string | undefined;
    Status: string | undefined;
    FullName: string | undefined;
    FirstName: string | undefined;
    LastName: string | undefined;
    Vehicle: string | undefined;
    EventDate: string | undefined;
    EventTime: string | undefined;
    StreetNumber: string | undefined;
    StreetName: string | undefined;
    City: string | undefined;
    Region: string | undefined;
    Start: string | undefined;
    End?: string | undefined;
    Change?: string | undefined;
    EventScore: string | undefined;
    Flagged: string | undefined;
    Dismissed: string | undefined;
    Comments: string | undefined;
}

export interface VideoEventForExport {
    videoEventTypePairings?: Array<{ videoEventTypeString?: string }>;
    driverName?: string;
    kphStart?: number;
    kphEnd?: number;
    companyLevelName?: string;
    displayId?: string;
    videoEventStatus?: string;
    vehicleDescription?: string;
    eventStartDate?: string;
    addressStreetNumber?: string;
    addressStreetName?: string;
    addressCityName?: string;
    addressRegion?: string;
    score?: number;
    isFlagged?: boolean;
    comments?: VideoEventCommentResponse[];
}

export const formatEventTypeToCommaSeparatedValue = (eventTypes: string[]): string => {
    if (!eventTypes || eventTypes.length === 0) return 'Unknown';

    return eventTypes
        .map((x) => {
            if (['forwardcollisionwarning', 'forward collision warning'].includes(x.toLowerCase())) {
                return 'FCW';
            }
            if (x.includes('Gforce')) {
                x = x.replace('Gforce', '');
            }
            if (x.includes('RightTurn')) {
                x = x.replace('RightTurn', 'Cornering');
            }
            return x.replace(/([A-Z])/g, ' $1').trim();
        })
        .join(', ');
};

export const formatComments = (comments: VideoEventCommentResponse[] | undefined): string => {
    if (!comments || comments.length === 0) return '';
    return comments.map((c) => `Commented by ${c.createdBy} on ${DateFormatter.date(moment(c.createdOn))}: ${c.comment}`).join('\n');
};

export const getCsvTitle = (filter: VideoEventFilterMultiselectRequest): string => {
    if (filter && filter.startDate && filter.endDate) {
        const formattedStartDate = moment(filter.startDate).utc().format('YYYY_MM_DD');
        const formattedEndDate = moment(filter.endDate).utc().format('YYYY_MM_DD');
        let title = 'Video Events';
        title += '_' + formattedStartDate;
        if (formattedStartDate !== formattedEndDate) {
            title += '-' + formattedEndDate;
        }
        return title;
    }
    return 'Video Events';
};

export const getExportHeaders = (isEncompass: boolean, isMetric: boolean, showCompanyLevel: boolean): { key: string; label: string }[] => {
    if (isEncompass) {
        const speedLabel = isMetric ? 'Kilometers Per Hour' : 'Miles Per Hour';
        return [
            { label: 'Event Type', key: 'EventType' },
            ...(showCompanyLevel ? [{ label: 'Company Level', key: 'CompanyLevel' }] : []),
            { label: 'ID', key: 'DisplayId' },
            { label: 'Status', key: 'Status' },
            { label: 'Driver Full Name', key: 'FullName' },
            { label: 'Driver First Name', key: 'FirstName' },
            { label: 'Driver Last Name', key: 'LastName' },
            { label: 'Vehicle', key: 'Vehicle' },
            { label: 'Event Date', key: 'EventDate' },
            { label: 'Event Time', key: 'EventTime' },
            { label: 'Street Number', key: 'StreetNumber' },
            { label: 'Street Name', key: 'StreetName' },
            { label: 'City', key: 'City' },
            { label: 'Region', key: 'Region' },
            { label: speedLabel, key: 'Start' },
            { label: 'Event Score', key: 'EventScore' },
            { label: 'Flagged', key: 'Flagged' },
            { label: 'Dismissed', key: 'Dismissed' },
            { label: 'Comments', key: 'Comments' },
        ];
    } else {
        const speedLabel = isMetric ? 'KPH' : 'MPH';
        return [
            { label: 'Event Type', key: 'EventType' },
            { label: 'ID', key: 'DisplayId' },
            { label: 'Status', key: 'Status' },
            { label: 'Driver Full Name', key: 'FullName' },
            { label: 'Driver First Name', key: 'FirstName' },
            { label: 'Driver Last Name', key: 'LastName' },
            { label: 'Vehicle', key: 'Vehicle' },
            { label: 'Event Date', key: 'EventDate' },
            { label: 'Event Time', key: 'EventTime' },
            { label: 'Street Number', key: 'StreetNumber' },
            { label: 'Street Name', key: 'StreetName' },
            { label: 'City', key: 'City' },
            { label: 'Region', key: 'Region' },
            { label: speedLabel + ' Start', key: 'Start' },
            { label: speedLabel + ' End', key: 'End' },
            { label: speedLabel + ' Change', key: 'Change' },
            { label: 'Event Score', key: 'EventScore' },
            { label: 'Flagged', key: 'Flagged' },
            { label: 'Dismissed', key: 'Dismissed' },
            { label: 'Comments', key: 'Comments' },
        ];
    }
};

export const mapEventToCsvRow = (val: VideoEventForExport, isEncompass: boolean, isMetric: boolean): CsvData => {
    const eventTypes = val.videoEventTypePairings?.map((eventType) => eventType?.videoEventTypeString ?? '');
    const nameArray =
        val.driverName === '' || val.driverName === ' ' || !val.driverName ? ['Unknown', 'Unknown'] : val.driverName.split(' ');
    const speedStart = unit.getSpeedValue(val.kphStart, isMetric);

    if (isEncompass) {
        return {
            EventType: formatEventTypeToCommaSeparatedValue(eventTypes ?? []),
            CompanyLevel: val.companyLevelName?.trim() ? val.companyLevelName : '',
            DisplayId: val.displayId ?? '',
            Status: val.videoEventStatus !== undefined ? getStatusLabel(val.videoEventStatus as VideoEventWorkflowStatusIdEnum) : 'Unknown',
            FullName: val.driverName?.trim() || 'Unknown',
            FirstName: nameArray[0],
            LastName: nameArray[nameArray.length - 1],
            Vehicle: val.vehicleDescription || 'Unknown',
            EventDate: DateFormatter.date(moment(val.eventStartDate) || moment()) + ' ',
            EventTime: DateFormatter.timeWithSecondsAndTimezone(moment(val.eventStartDate) || moment()) + ' ',
            StreetNumber: val.addressStreetNumber || '',
            StreetName: val.addressStreetName || '',
            City: val.addressCityName || '',
            Region: val.addressRegion || '',
            Start: unit.formatSpeedString(speedStart),
            EventScore: val.score?.toString() || '',
            Flagged: val.isFlagged ? 'Flagged' : 'Not Flagged',
            Dismissed: val.videoEventStatus !== 'Dismissed' ? 'False' : 'True',
            Comments: formatComments(val.comments),
        };
    } else {
        const speedEnd = unit.getSpeedValue(val.kphEnd, isMetric);
        const change = Math.abs(Math.round(speedEnd) - Math.round(speedStart)).toString();
        return {
            EventType: formatEventTypeToCommaSeparatedValue(eventTypes ?? []),
            DisplayId: val.displayId ?? '',
            Status: val.videoEventStatus !== undefined ? getStatusLabel(val.videoEventStatus as VideoEventWorkflowStatusIdEnum) : 'Unknown',
            FullName: val.driverName?.trim() || 'Unknown',
            FirstName: nameArray[0],
            LastName: nameArray[nameArray.length - 1],
            Vehicle: val.vehicleDescription || 'Unknown',
            EventDate: DateFormatter.date(moment(val.eventStartDate) || moment()) + ' ',
            EventTime: DateFormatter.timeWithSecondsAndTimezone(moment(val.eventStartDate) || moment()) + ' ',
            StreetNumber: val.addressStreetNumber || '',
            StreetName: val.addressStreetName || '',
            City: val.addressCityName || '',
            Region: val.addressRegion || '',
            Start: unit.formatSpeedString(speedStart),
            End: unit.formatSpeedString(speedEnd),
            Change: change,
            EventScore: val.score?.toString() || '',
            Flagged: val.isFlagged ? 'Flagged' : 'Not Flagged',
            Dismissed: val.videoEventStatus !== 'Dismissed' ? 'False' : 'True',
            Comments: formatComments(val.comments),
        };
    }
};
